import type { TicketState } from './api.js';

/**
 * Duration formatting. Everything the API reports in minutes or hours is
 * shown here, and it is deliberately compact: a queue with 40 rows lives or
 * dies on how quickly a number can be read.
 */
export function hours(h: number | null | undefined, digits = 1): string {
  if (h === null || h === undefined) return '—';
  if (h < 1 / 60) return '<1m';
  if (h < 1) return `${Math.round(h * 60)}m`;
  if (h < 48) return `${h.toFixed(digits).replace(/\.0$/, '')}h`;
  const d = h / 24;
  return `${d.toFixed(d < 10 ? 1 : 0)}d`;
}

export function minutes(m: number | null | undefined): string {
  return hours(m === null || m === undefined ? null : m / 60);
}

export function pct(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(digits)}%`;
}

/**
 * A share where the two ends mean what they say: only an actual zero prints
 * 0%, and only an actual whole prints 100%.
 *
 * `pct` is right for a measurement. It is wrong for a share sitting next to
 * the count it came from — 24,889 of 24,985 rendered as "100%" beside a badge
 * reading "3 values" makes the row look broken, and 15 of 25,054 rendered as
 * "0%" hides the finding entirely.
 */
export function sharePct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  if (v <= 0) return '0%';
  if (v >= 1) return '100%';
  return `${Math.min(99, Math.max(1, Math.round(v * 100)))}%`;
}

export function num(v: number | string | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'string' ? Number(v) : v;
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US');
}

/** Relative time, US English, compact. */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  const secs = Math.round((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * How long until a moment in the future.
 *
 * `ago` reads the other direction and answers "just now" for anything not yet
 * past, which is how a ticket parked for nine days came out as "Back just
 * now" in the queue. A date in the future needs its own words.
 */
export function until(iso: string | null | undefined): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60_000);
  if (mins < 60) return `in ${Math.max(1, mins)}m`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.round(hrs / 24);
  if (days < 14) return `in ${days}d`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * A calendar date, rendered as the calendar date it is.
 *
 * `new Date('2026-03-01')` is parsed as UTC MIDNIGHT, and west of Greenwich
 * that renders as the day before: a baseline pinned for 1–31 March displayed
 * as "Feb 28 – Mar 30". Every Postgres `date` column reaches the browser as
 * `YYYY-MM-DD` — a baseline window, an article's review-by — so the bug was
 * silent and systematic.
 *
 * A date-only string has no timezone and must not be given one. It is split
 * and constructed in LOCAL time, which is the only reading under which
 * "1 March" means 1 March. A full timestamp still goes through `Date`,
 * because an instant genuinely does have a zone.
 */
export function dateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  const plain = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  const d = plain
    ? new Date(Number(plain[1]), Number(plain[2]) - 1, Number(plain[3]))
    : new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * State labels. The three pending_* states get distinct wording because the
 * distinction between them is the whole point (FR-RPT-03) — "Pending" alone is
 * what makes the current 143-hour p90 unexplainable.
 */
export const STATE_LABEL: Record<TicketState, string> = {
  new: 'New',
  open: 'Open',
  pending_requester: 'Waiting on requester',
  pending_external: 'Blocked externally',
  pending_internal: 'Waiting internally',
  pending_event: 'Waiting for an event',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

export const STATE_SHORT: Record<TicketState, string> = {
  new: 'New',
  open: 'Open',
  pending_requester: 'Requester',
  pending_external: 'External',
  pending_internal: 'Internal',
  pending_event: 'Event',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** Tailwind classes per state. Never colour alone — the label carries it too. */
export function stateClasses(state: TicketState): string {
  switch (state) {
    case 'new':
      return 'bg-teal-50 text-teal-700 dark:bg-teal-900/40 dark:text-teal-200';
    case 'open':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-800/50 dark:text-teal-100';
    case 'pending_event':
      // Quiet, like the internal waits: this is the most under-control a
      // blocked ticket can be — the date is known and the return is owned.
      return 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-200';
    case 'pending_requester':
    case 'pending_internal':
      return 'bg-stone-100 text-stone-600 dark:bg-stone-700/50 dark:text-stone-300';
    case 'pending_external':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200';
    case 'resolved':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200';
    case 'closed':
      return 'bg-stone-100 text-stone-500 dark:bg-stone-800 dark:text-stone-400';
    case 'cancelled':
      return 'bg-stone-100 text-stone-400 line-through dark:bg-stone-800 dark:text-stone-500';
  }
}

export const SOURCE_LABEL: Record<string, string> = {
  email: 'Email',
  portal: 'Portal',
  phone: 'Phone',
  teams: 'Teams',
  api: 'API',
  automation: 'Automation',
  system: 'System',
};

export function initials(name: string | null, email: string | null): string {
  if (name && name.trim()) {
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? '') + (parts.at(-1)?.[0] ?? '')).toUpperCase();
  }
  return (email ?? '?').slice(0, 2).toUpperCase();
}

/** Display a person without ever inventing a name (FR-ID-03). */
export function personLabel(name: string | null | undefined, email: string | null | undefined): string {
  if (name && name.trim()) return name;
  return email ?? 'Unknown';
}
